import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@truefit/http-utils';
import useCurrentGameId from '@/shared/hooks/useCurrentGameId';
import { ClipModel } from '@/features/clips/types';
import { AddClipToGroupInput } from '../types';

interface AddClipToGroupParams {
  playbyplayId: string;
  clipGroupId: number;
  data: AddClipToGroupInput;
}

const useAddClipToGroupMutation = () => {
  const queryClient = useQueryClient();
  const gameId = useCurrentGameId();

  return useMutation({
    mutationFn: async ({ playbyplayId, clipGroupId, data }: AddClipToGroupParams) => {
      const response = await post<{ success: boolean; clip: ClipModel; message?: string }>(
        `/v1.0/games/${gameId}/play-by-play/${playbyplayId}/clip-groups/${clipGroupId}/clips`,
        data
      );

      // Validate response - throw error if success is false or clip is missing
      if (!response.data.success || !response.data.clip) {
        throw new Error(response.data.message || 'Failed to create clip - no clip data returned');
      }

      return response.data.clip;
    },
    onSuccess: (_, variables) => {
      // Invalidate the clip groups query to refresh clip counts
      queryClient.invalidateQueries({
        queryKey: ['Games', gameId, 'PlayByPlay', variables.playbyplayId, 'ClipGroups']
      });
      // Invalidate game-level clip groups
      queryClient.invalidateQueries({
        queryKey: ['Games', gameId, 'ClipGroups']
      });
      // Invalidate clips query
      queryClient.invalidateQueries({
        queryKey: ['clips']
      });
    },
  });
};

export default useAddClipToGroupMutation;
